import React from 'react';
import { Box, Divider, makeStyles } from '@material-ui/core';
import {
  RESTAURANTS_NEAR_YOU_LABEL,
  SEARCH_RESULT_OTHER_LABEL,
} from '../../../assets/copy';
import { RestaurantCard } from './RestaurantCard';
import Typography from '../../../components/overrides/Typography';
import { useItemsInMarket } from '../../../utils/useItemsInMarket';
import { useHistory } from 'react-router';
import ClearSearch from './ClearSearch';
import { useSelector } from 'react-redux';
import LocalMarketEmpty from './LocalMarketEmpty';
import OtherMarketPagination from './OtherMarketPagination.';
import { useSearchParams } from '../../../components/search/helpers';
import {
  trackClickWithDescription,
  trackPageView,
} from '../../../utils/useOneTag';
import useCustomIntersectionObserver from '../../../utils/useCustomIntersectionObserver';

const TIER_1_LABEL = 'tier_1';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    '& > *:not(hr)': {
      padding: theme.spacing(0, 2),
    },
    maxWidth: theme.spacing(82),
    margin: 'auto',
    marginTop: 0,
    [theme.breakpoints.up('xxl')]: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(9),
    },
  },
  cta: {
    '& svg': {
      display: 'none',
    },
  },
  restaurantList: {
    margin: theme.spacing(1, 0),
    '& > *:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.digitalGray[20]}`,
    },
  },
  divider: {
    backgroundColor: theme.palette.beacon.warmGray,
  },
  suggestionsContainer: {
    minHeight: 'calc(100vh - 265px)',
    [theme.breakpoints.up('xxl')]: {
      minHeight: 'calc(100vh - 440px)',
    },
  },
}));

/**
 * NoResults component
 *
 * @param {object} props
 * @param {object} [props.classes] Extends default styles
 * @returns {JSX.Element}
 */
export const NoResults = ({ classes: classesOverride, clearSearch }) => {
  const classes = useStyles({ classes: classesOverride });

  const { inLocalMarket, pages } = useSelector((state) => state.venues);
  const { pageNumber } = useSearchParams();
  const { currentQuery } = useSelector((state) => state.search);
  const { venues } = useItemsInMarket();
  const history = useHistory();

  let recommendedRestaurants;

  const showSuggestionsInOtherMarket =
    inLocalMarket === false && currentQuery?.length > 0 && pages?.length > 0;

  if (!showSuggestionsInOtherMarket) {
    recommendedRestaurants = venues
      .filter((venue) => venue.labels?.includes(TIER_1_LABEL))
      .splice(0, 3);
  } else {
    recommendedRestaurants = pages?.[pageNumber];
  }

  const clearSearchAction = () => {
    trackClickWithDescription(
      'no-results-clear-search',
      'button',
      showSuggestionsInOtherMarket ? 'other-market' : 'local-market'
    );
    clearSearch();
  };

  const elementRef = useCustomIntersectionObserver(
    () => {
      if (showSuggestionsInOtherMarket) {
        trackPageView('No Results Page - Results in other cities');
      } else {
        trackPageView('No Results Page - Restaurants Near You');
      }
    },
    { threshold: 0.8, triggerOnce: true }
  );

  return (
    <Box className={classes.root} ref={elementRef}>
      {!showSuggestionsInOtherMarket ? (
        <ClearSearch clearSearchFunction={clearSearchAction} />
      ) : (
        <LocalMarketEmpty clearSearchFunction={clearSearchAction} />
      )}
      <Divider className={classes.divider} />
      <Box className={classes.suggestionsContainer}>
        {showSuggestionsInOtherMarket ? (
          <Typography variant="medium1Semibold">
            {SEARCH_RESULT_OTHER_LABEL}
          </Typography>
        ) : (
          <>
            {recommendedRestaurants?.length > 0 && (
              <Typography 
                variant="medium1Semibold"
                component="h2">
                  {RESTAURANTS_NEAR_YOU_LABEL}
              </Typography>
            )}
          </>
        )}

        <Box className={classes.restaurantList}>
          {recommendedRestaurants.map((venue, index) => (
            <RestaurantCard
              key={venue.id + index}
              venue={venue}
              onClick={() => {
                trackClickWithDescription(
                  'no-results-restaurant-card',
                  'button',
                  venue.name
                );
                history.push(`/venue/${venue.id}`, { isSearchResult: true });
              }}
              shouldShowCityInDescription={showSuggestionsInOtherMarket}
            />
          ))}
        </Box>
        {showSuggestionsInOtherMarket && <OtherMarketPagination />}
      </Box>
    </Box>
  );
};

export default NoResults;
